import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled/macro";
import { DateTime } from "luxon";
import {
  Button,
  LoadingIndicator,
  tokens,
} from "@sunrun/experience-ui-components";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import {
  CancelSiteSurveyAppointmentRequestData,
  SiteSurveySlotRequestData,
} from "../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { Card } from "../molecules/Card";
import { SiteSurveyCancelAppointmentModal } from "../molecules/SiteSurveyCancelAppointmentModal";
import {
  AppointmentActionErrorModal,
  SCHEDULE_APPOINTMENT_ERROR_DESCRIPTION,
  CHECKOUT_SITE_SURVEY_TEST_IDS,
  ConfirmRescheduleAppointmentModal,
  HEADER_DATE_FORMAT,
  SCHEDULE_SUBTITLE,
  SCHEDULE_SUBTITLE_NON_SOLAR,
  SiteSurveyScheduledCard,
  SiteSurveySlotPicker,
  SLOT_TIME_FORMAT,
  SLOTS_ERROR,
  SLOTS_ERROR_BUTTON,
  SLOTS_UNAVAILABLE_BUTTON,
  SLOTS_UNAVAILABLE_LINE_1,
  SLOTS_UNAVAILABLE_LINE_2,
  SLOTS_UNAVAILABLE_MESSAGE,
  SlotsUnavailableModal,
  useSiteSurveyCancelFlow,
  useSiteSurveySlotPickerState,
} from "../molecules/siteSurveyScheduling";
import { ErrorModal } from "components/molecules/ErrorModal";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import {
  useSiteSurveyAppointments,
  useSiteSurveySlots,
} from "checkout/api/siteSurvey";
import {
  createSiteSurveyAppointment,
  cancelSiteSurveyAppointment,
  rescheduleSiteSurveyAppointment,
} from "services/prospect";
import { useUsageData } from "utils/swrHooks";
import { useAuthToken } from "hooks/useAuthToken";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { toRejectableError } from "utils/errorUtils";

const toError = (error: unknown): Error | undefined => {
  if (!error) return undefined;
  return toRejectableError(error);
};

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
  margin-bottom: 1em;
  @media (max-width: 800px) {
    flex-direction: column;
    gap: 0;
  }
`;
const Actions = styled.div`
  margin-top: 1em;
  display: flex;
  justify-content: end;
`;
const TimeZone = styled(Card)`
  display: inline-block;
  padding: 1em;
  background-color: ${tokens.TINTS_CHARGE_BLUE_70};
  border: 1px solid ${tokens.TINTS_CHARGE_BLUE_40};
  white-space: nowrap;
`;

const ScheduleAppointment: React.FC = () => {
  const authKey = useAuthToken("HYBRID");
  const {
    currentTask,
    hasNextTask,
    navigateToNextTask,
    refreshTasks,
    isNonSolarOffer,
  } = useCheckoutContext();
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 930 });
  const COLUMNS = isMobile ? 1 : isTablet ? 2 : 3;
  const { prospectId } = useParams();
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loadingCancelAppt, setLoadingCancelAppt] = useState(false);
  const [loadingReschedule, setLoadingReschedule] = useState(false);
  const [showConfirmRescheduleModal, setShowConfirmRescheduleModal] =
    useState(false);
  const [siteSurveyLoading, setSiteSurveyLoading] = useState(false);
  const [showScheduleErrorModal, setShowScheduleErrorModal] = useState(false);
  const [showRescheduleErrorModal, setShowRescheduleErrorModal] =
    useState(false);
  const [showAppointmentLoadErrorModal, setShowAppointmentLoadErrorModal] =
    useState(false);
  const [showSlotsErrorDetailsModal, setShowSlotsErrorDetailsModal] =
    useState(false);

  const {
    siteSurveyReadyToBeScheduled,
    siteSurveyAppointment,
    siteSurveyAppointmentsLoading,
    mutateSiteSurveyAppointments,
    siteSurveyApointmentError,
  } = useSiteSurveyAppointments(prospectId);

  const slots = useSiteSurveySlots(
    prospectId || "",
    siteSurveyReadyToBeScheduled?.appointmentId || ""
  );

  const { prospect, isLoading: isProspectLoading } = useUsageData(prospectId);

  const {
    startDate,
    selectedSlot,
    setSelectedSlot,
    incrementDate,
    decrementDate,
  } = useSiteSurveySlotPickerState(
    slots.data,
    COLUMNS,
    undefined,
    undefined,
    prospect?.timeZoneId
  );

  const cancelAppointment = useCallback(
    (reason: string) => {
      if (!siteSurveyAppointment || !prospectId) {
        return Promise.reject(new Error("missing appointment or prospect"));
      }
      setLoadingCancelAppt(true);
      const requestObj = {
        ...siteSurveyAppointment,
        cancellationReason: reason,
        isComplexAppointment: false,
      } as CancelSiteSurveyAppointmentRequestData;

      return cancelSiteSurveyAppointment(
        authKey?.token ?? "",
        prospectId,
        siteSurveyAppointment.appointmentId,
        requestObj
      )
        .then(() => {
          mutateSiteSurveyAppointments();
          refreshTasks();
        })
        .finally(() => {
          setLoadingCancelAppt(false);
        });
    },
    [
      authKey?.token,
      mutateSiteSurveyAppointments,
      prospectId,
      refreshTasks,
      siteSurveyAppointment,
    ]
  );

  useEffect(() => {
    if (siteSurveyApointmentError) {
      setShowAppointmentLoadErrorModal(true);
    }
  }, [siteSurveyApointmentError]);

  const { cancelApptReason, onUnschedule, clearCancelError, showCancelError } =
    useSiteSurveyCancelFlow({
      cancelAppointment,
      onCancelModalClose: () => setShowCancelModal(false),
    });

  const onSchedule = useCallback(() => {
    if (!selectedSlot || !prospect?.salesRep || !prospectId) {
      return;
    }

    setSiteSurveyLoading(true);

    const { appointmentId, arrivalStartDate, arrivalEndDate } = selectedSlot;

    const requestObj: SiteSurveySlotRequestData = {
      appointmentId,
      arrivalStartDate,
      arrivalEndDate,
      appointmentType: "Site Survey",
      isComplexAppointment: false,
    };

    createSiteSurveyAppointment(authKey?.token ?? "", prospectId, requestObj)
      .then(() => {
        mutateSiteSurveyAppointments();
        refreshTasks();
      })
      .catch(() => {
        setShowScheduleErrorModal(true);
      })
      .finally(() => {
        setSiteSurveyLoading(false);
      });
  }, [
    selectedSlot,
    prospect?.salesRep,
    prospectId,
    authKey?.token,
    mutateSiteSurveyAppointments,
    refreshTasks,
  ]);

  const onRetryAfterSlotsError = useCallback(() => {
    mutateSiteSurveyAppointments();
    slots.retry();
  }, [mutateSiteSurveyAppointments, slots]);

  const onShowSlotsErrorDetails = useCallback(() => {
    setShowSlotsErrorDetailsModal(true);
  }, []);

  const onReschedule = useCallback(() => {
    setLoadingReschedule(true);
    setShowConfirmRescheduleModal(false);

    if (!siteSurveyAppointment?.appointmentId) {
      console.error("No appointment ID to reschedule");
      return;
    }

    rescheduleSiteSurveyAppointment(authKey?.token ?? "", {
      appointmentId: siteSurveyAppointment.appointmentId,
      rescheduleReason: "Customer Reschedule",
      isComplexAppointment: false,
    })
      .then(() => {
        mutateSiteSurveyAppointments();
        refreshTasks();
      })
      .catch(() => {
        setShowRescheduleErrorModal(true);
      })
      .finally(() => {
        setLoadingReschedule(false);
      });
  }, [
    authKey?.token,
    mutateSiteSurveyAppointments,
    refreshTasks,
    siteSurveyAppointment?.appointmentId,
  ]);

  if (
    !currentTask ||
    siteSurveyAppointmentsLoading ||
    isProspectLoading ||
    siteSurveyLoading ||
    loadingReschedule
  ) {
    return <LoadingIndicator color="black" />;
  }

  return (
    <>
      <Header>
        <p>
          {isNonSolarOffer ? SCHEDULE_SUBTITLE_NON_SOLAR : SCHEDULE_SUBTITLE}
        </p>
        <TimeZone>
          {DateTime.now().setZone(prospect?.timeZoneId).offsetNameLong}
        </TimeZone>
      </Header>
      {siteSurveyAppointment && (
        <SiteSurveyScheduledCard
          appointment={siteSurveyAppointment}
          timeZoneId={prospect?.timeZoneId}
          onRequestReschedule={() => setShowConfirmRescheduleModal(true)}
          onRequestCancel={() => setShowCancelModal(true)}
          cancelButtonTestId={CHECKOUT_SITE_SURVEY_TEST_IDS.cancelButton}
        />
      )}
      {!siteSurveyAppointment && (
        <SiteSurveySlotPicker
          slotsRetryTestId={CHECKOUT_SITE_SURVEY_TEST_IDS.slotsRetry}
          showUnavailableModalTestId={
            CHECKOUT_SITE_SURVEY_TEST_IDS.showUnavailableModal
          }
          columns={COLUMNS}
          timeZoneId={prospect?.timeZoneId}
          startDate={startDate}
          incrementDate={incrementDate}
          decrementDate={decrementDate}
          slots={slots.data}
          slotsLoading={slots.isLoading}
          slotsError={slots.error}
          onRetrySlots={onRetryAfterSlotsError}
          onShowErrorDetails={slots.error ? onShowSlotsErrorDetails : undefined}
          selectedSlot={selectedSlot}
          onSelectSlot={setSelectedSlot}
          emptySlotsMode="contactTeam"
          onContactTeam={() => setShowUnavailableModal(true)}
        />
      )}
      <Actions>
        {!currentTask.isComplete && (
          <Button
            data-testid="select-slot"
            disabled={!selectedSlot}
            onClick={onSchedule}
          >
            Schedule
          </Button>
        )}
        {currentTask.isComplete && hasNextTask && (
          <Button data-testid="continue" onClick={navigateToNextTask}>
            Confirm
          </Button>
        )}
      </Actions>
      <SlotsUnavailableModal
        open={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
      />
      {showCancelModal && siteSurveyAppointment && (
        <SiteSurveyCancelAppointmentModal
          loading={loadingCancelAppt}
          cancellationReasons={
            siteSurveyAppointment.cancellationReasons?.map((reason) => ({
              label: reason,
              value: reason,
            })) || [{ label: "", value: "" }]
          }
          name={siteSurveyAppointment.homeownerName || ""}
          onClose={() => setShowCancelModal(false)}
          onSubmit={onUnschedule}
        />
      )}
      <ConfirmRescheduleAppointmentModal
        testId={CHECKOUT_SITE_SURVEY_TEST_IDS.confirmRescheduleModal}
        confirmRescheduleTestId={
          CHECKOUT_SITE_SURVEY_TEST_IDS.confirmRescheduleButton
        }
        open={showConfirmRescheduleModal}
        onConfirm={onReschedule}
        onClose={() => setShowConfirmRescheduleModal(false)}
      />
      {showScheduleErrorModal && (
        <AppointmentActionErrorModal
          testId={CHECKOUT_SITE_SURVEY_TEST_IDS.scheduleErrorModal}
          title="Something went wrong creating your appointment"
          description={SCHEDULE_APPOINTMENT_ERROR_DESCRIPTION}
          onRetry={() => {
            setShowScheduleErrorModal(false);
            onSchedule();
          }}
          onDismiss={() => setShowScheduleErrorModal(false)}
        />
      )}
      {showRescheduleErrorModal && (
        <AppointmentActionErrorModal
          testId={CHECKOUT_SITE_SURVEY_TEST_IDS.rescheduleErrorModal}
          title="Something went wrong rescheduling your appointment"
          description={SCHEDULE_APPOINTMENT_ERROR_DESCRIPTION}
          onRetry={() => {
            setShowRescheduleErrorModal(false);
            onReschedule();
          }}
          onDismiss={() => setShowRescheduleErrorModal(false)}
        />
      )}
      {showCancelError && cancelApptReason != null && (
        <AppointmentActionErrorModal
          testId={CHECKOUT_SITE_SURVEY_TEST_IDS.cancelErrorModal}
          title="Something went wrong cancelling your appointment"
          description={SCHEDULE_APPOINTMENT_ERROR_DESCRIPTION}
          onRetry={() => {
            clearCancelError();
            onUnschedule(cancelApptReason);
          }}
          onDismiss={clearCancelError}
        />
      )}
      {showAppointmentLoadErrorModal && (
        <ErrorModal
          context="Schedule Site Audit"
          action="loading appointments"
          error={toError(siteSurveyApointmentError)}
          errorFromAnotherSystem
          onRetry={() => {
            setShowAppointmentLoadErrorModal(false);
            mutateSiteSurveyAppointments();
          }}
          onClose={() => setShowAppointmentLoadErrorModal(false)}
        />
      )}
      {showSlotsErrorDetailsModal && (
        <ErrorModal
          context="Schedule Site Audit"
          action="loading appointment slots"
          error={toError(slots.error)}
          errorFromAnotherSystem
          onRetry={() => {
            setShowSlotsErrorDetailsModal(false);
            onRetryAfterSlotsError();
          }}
          onClose={() => setShowSlotsErrorDetailsModal(false)}
        />
      )}
    </>
  );
};

export {
  HEADER_DATE_FORMAT,
  SLOTS_ERROR,
  SLOTS_ERROR_BUTTON,
  SLOTS_UNAVAILABLE_LINE_1,
  SLOTS_UNAVAILABLE_LINE_2,
  SLOTS_UNAVAILABLE_BUTTON,
  SLOTS_UNAVAILABLE_MESSAGE,
  SLOT_TIME_FORMAT,
  ScheduleAppointment,
};
